import { useState, useEffect, useCallback, useRef } from 'react';
import * as chatService from '../services/chatService';
import { useChatConsent } from '../consent-manager';

/**
 * Custom hook for chat functionality
 * Uses functional consent from ConsentContext for cookie management
 * @returns {Object} - Chat state and functions
 */
const useChatBot = () => {
  // Get consent state directly from the consent manager
  const { hasConsent: cookiesAccepted, consentLoaded } = useChatConsent();
  // Chat state
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [ephemeralThreadId, setEphemeralThreadId] = useState(null);
  
  // Refs to track history loading status
  const historyLoadedRef = useRef(false);
  
  /**
   * Load conversation history from the backend
   * Only attempts to load history if cookies are accepted
   * Also ensures cookies are properly initialized even if user hasn't chatted yet
   */
  const loadConversationHistory = useCallback(async () => {
    console.log('Loading conversation history with consent state:', cookiesAccepted);
    
    // Only load history if cookies are accepted
    if (!cookiesAccepted) {
      console.log('Cookies not accepted, using ephemeral chat mode');
      // Set history as loaded to prevent repeated attempts
      historyLoadedRef.current = true;
      setHistoryLoaded(true);
      setLoading(false);
      return;
    }
    
    if (historyLoaded) {
      console.log('History already loaded, skipping');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Get conversation history - pass cookies accepted state
      // This prevents trying to load history when consent is denied
      const historyResponse = await chatService.getConversationHistory(cookiesAccepted);
      // console.log('History response:', historyResponse);
      
      // If we have cookiesAccepted but no history, that's fine - it just means
      // this is the first chat with consent granted
      
      if (historyResponse.success) {
        if (historyResponse.messages && historyResponse.messages.length > 0) {
          // We have history - transform to match the expected format with 'sender' instead of 'role'
          const formattedMessages = historyResponse.messages.map(msg => ({
            sender: msg.sender_type === 'visitor' ? 'visitor' : 'bot',
            content: msg.content,
            timestamp: msg.created_at || new Date().toISOString()
          }));
          
          // console.log('Formatted history messages:', formattedMessages);
          setMessages(formattedMessages);
          // console.log('Conversation history loaded, messages:', historyResponse.messages.length);
        } else {
          // console.log('No existing conversation history found');
        }
        historyLoadedRef.current = true;
        setHistoryLoaded(true);
      } else {
        console.error('Failed to load conversation history:', historyResponse.error);
        setError('Failed to load conversation history');
      }
    } catch (err) {
      console.error('Error in loadConversationHistory:', err);
      setError('Failed to load conversation history');
    } finally {
      setLoading(false);
    }
  }, [cookiesAccepted, historyLoaded]);

  /**
   * Send a message to the AI assistant
   * @param {string} message - The message to send
   */
  const sendUserMessage = useCallback(async (message) => {
    if (!message.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Sending message with cookie consent state:', cookiesAccepted);
      
      // Add user message to the local state first - using sender format for ChatBot component
      const userMessage = { sender: 'visitor', content: message, timestamp: new Date().toISOString() };
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      
      // Send message to the API with the current consent state
      // This prevents the chat from recreating cookies when consent is denied
      const response = await chatService.sendMessage(message, cookiesAccepted, ephemeralThreadId);
      
      if (!response.success) {
        console.error('Error from chat API:', response.error);
        const errorMessage = {
          sender: 'bot',
          content: 'Sorry, I encountered an error processing your request. Please try again later.',
          timestamp: new Date().toISOString()
        };
        setMessages([...updatedMessages, errorMessage]);
        setLoading(false);
        return;
      }
      
      // Add the bot response - using sender format for ChatBot component compatibility
      // console.log('Got response:', response);
      
      // Extract the message content from the response structure
      let botContent = '';
      if (response.response && response.response.content) {
        // New API format
        botContent = response.response.content;
      } else if (response.message) {
        // Old API format
        botContent = response.message;
      } else {
        // Fallback if no content is found
        botContent = 'I received your message but was unable to generate a proper response.';
        console.error('Unexpected API response format:', response);
      }
      
      const botMessage = {
        sender: 'bot', // Use sender instead of role for ChatBot component compatibility
        content: botContent,
        timestamp: new Date().toISOString()
      };
      
      // console.log('Created bot message:', botMessage);
      
      // Keep track of ephemeral thread ID when cookies are not accepted
      if (!cookiesAccepted && response.thread_id) {
        setEphemeralThreadId(response.thread_id);
        // console.log('Using ephemeral thread ID:', response.thread_id);
      }
      
      setMessages([...updatedMessages, botMessage]);
    } catch (err) {
      console.error('Error in sendUserMessage:', err);
      setError('Failed to send message');
    } finally {
      setLoading(false);
    }
  }, [cookiesAccepted, ephemeralThreadId, messages]);

  // Update when cookie consent changes
  useEffect(() => {
    // Only react to consent changes once consent is fully loaded
    if (!consentLoaded) {
      // console.log('Waiting for consent to be fully loaded...');
      return;
    }

    // console.log('Consent state changed in useChatBot:', cookiesAccepted);
    
    if (cookiesAccepted) {
      console.log('Cookies accepted, loading conversation history');
      loadConversationHistory();
    } else {
      console.log('Cookies not accepted, using ephemeral chat mode');
      // Reset any previous conversation history when consent is revoked
      if (messages.length > 0 && historyLoaded) {
        console.log('Clearing conversation history due to consent revocation');
        setMessages([]);
        setHistoryLoaded(false);
        historyLoadedRef.current = false;
      }
    }
  }, [cookiesAccepted, consentLoaded, loadConversationHistory, messages.length, historyLoaded]);

  return {
    messages,
    loading,
    error,
    sendMessage: sendUserMessage,
    loadConversationHistory
  };
};

export default useChatBot;
